/* eslint no-nested-ternary: "off" */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button";
import appLogo from "assets/logo/logo-only.png";
import "./index.scss";
import { getOrder } from "api/Order";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { getFarm } from "api/Farm";
import { getField } from "api/Field";
import { parseFieldArea } from "utils";
import Loading from "components/Loading";
import OrderReceipt from "pages/Order/OrderReceipt";

const OrderReviewPage = () => {
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();
  const { id: orderUrId, type } = useParams();
  const [order, setOrder] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [farms, setFarms] = useState(null);
  const [farmsLoading, setFarmsLoading] = useState([]);
  const [orderTotal, setOrderTotal] = useState("");
  const [viewReceipt, setViewReceipt] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const formatStartDate = startDate
    ? format(new Date(startDate), "dd MMM yyyy")
    : "";
  const formatEndDate = endDate ? format(new Date(endDate), "dd MMM yyyy") : "";
  const [token, setToken] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      const tokenData = await getAccessTokenSilently();
      setToken(tokenData);
    };

    fetchToken();
  });

  const fetchFarmsAndFields = useCallback(
    async (fieldIds) => {
      const farmIds = [];
      const newFarmsData = [];

      setFarmsLoading(true);
      await Promise.all(
        fieldIds.map(async (id) => {
          try {
            const field = await getField(id, token);
            farmIds.push({ farmId: field?.properties?.farm_id, fields: field });
          } catch (e) {
            toast.error(e?.response);
          }
        })
      );
      if (farmIds.length > 0) {
        const uniqueId = farmIds.map((id) => id.farmId);
        const uniqueIds = Array.from(new Set(uniqueId));
        await Promise.all(
          uniqueIds.map(async (farm) => {
            try {
              const farmsData = await getFarm(farm, token);
              newFarmsData.push({ newFarms: farmsData });
            } catch (e) {
              toast.error(e?.response);
            }
          })
        );
      }
      if (newFarmsData.length > 0) {
        const mappedFarms = new Map(
          newFarmsData.map(({ newFarms }) => [
            newFarms?.properties?.farm_id,
            { newFarms, fields: [] },
          ])
        );
        farmIds.forEach(({ farmId, fields }) => {
          mappedFarms.get(farmId).fields.push(...[fields].flat());
          setFarms([...mappedFarms.values()]);
        });
      }
      setFarmsLoading(false);
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    const fetchOrderDetails = async (orderId) => {
      try {
        const orderDetails = await getOrder(orderId, token);
        setOrder(orderDetails.data);
        setPaymentDetails(orderDetails.payment);
        const formatTotal = orderDetails.data.order_subtotal / 100;
        setOrderTotal(formatTotal);
        setStartDate(orderDetails?.data?.start_capture_date);
        setEndDate(orderDetails?.data?.end_capture_date);
        fetchFarmsAndFields(orderDetails?.data?.specification?.field_ids);
      } catch (e) {
        toast.error(e?.response);
      }
    };

    if (orderUrId) {
      fetchOrderDetails(orderUrId);
    }
  }, [orderUrId, token, fetchFarmsAndFields, history]);

  const encodeObject = (object) => {
    return encodeURIComponent(JSON.stringify(object))
      .replace(/'/g, "%27")
      .replace(/"/g, "%22");
  };

  const mapUrl = (field) => {
    const baseUrl =
      "https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static";
    const geojson = {
      type: "Feature",
      properties: {
        stroke: "#09BA51",
        "stroke-width": 2,
        "stroke-opacity": 1.0,
      },
      geometry: field.geometry,
    };
    const bBox = field.bbox;
    const mapStyle = "100x100@2x?padding=8";
    const mapboxToken = process.env.REACT_APP_MAPGL_ACCESS_TOKEN;

    return `${baseUrl}/geojson(${encodeObject(
      geojson
    )})/[${bBox}]/${mapStyle}&access_token=${mapboxToken}`;
  };

  const renderFile = (stats) => {
    if (stats === "pending" || stats === "cancelled") {
      return "View Invoice";
    }
    return "View Receipt";
  };

  return (
    <div className="order-review" data-testid="order-review">
      <div className="header-panel">
        <span className="icon-image" />
        <h1 className="title">High Resolution Ordering</h1>
        <img className="app-logo" src={appLogo} alt="alt" />
      </div>
      <div className="main-container">
        <div className="content-wrapper">
          {type === "success" && (
            <div className="bubble-wrap">
              Your order has been placed successfully. You will be contacted
              shortly.
            </div>
          )}
          <h2 className="page-title">Order Review</h2>
          <div className="group-divider">Farm Details</div>
          {farmsLoading ? (
            <div className="farm-group">
              <Loading style={{ marginLeft: "120px" }} />
            </div>
          ) : farms && farms.length > 0 ? (
            farms?.map((data) => (
              <div className="farm-group">
                <label className="label">Farm Name</label>
                <h2 className="farm-name">
                  {data?.newFarms?.properties?.farm_name}
                </h2>
                {data.fields.map((field) => (
                  <div className="field-selection">
                    <div className="field-header">
                      <p className="column">Field Name</p>
                      <p className="column">Crop Type</p>
                      <p className="column">Area</p>
                    </div>
                    <div className="field-container">
                      <div className="field-item">
                        <p className="column">
                          <img
                            className="field-image"
                            src={mapUrl(field)}
                            alt="field-map"
                          />
                          {field?.properties.field_name}
                        </p>
                        <p className="column">
                          {field?.properties?.field_crop_type === ""
                            ? "No Crop Type"
                            : field.properties?.field_crop_type}
                        </p>
                        <p className="column">
                          {`${parseFieldArea(field?.properties?.area)} Ha`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : null}
          <div className="group-divider">Capture Range</div>
          <div className="text-group">
            <div className="left">
              <label className="label">Start Date</label>
              <p className="text">{formatStartDate}</p>
            </div>
            <div className="right">
              <label className="label">End Date</label>
              <p className="text">{formatEndDate}</p>
            </div>
          </div>
          <div className="group-divider">Order Summary</div>
          <div className="text-group">
            <div className="left">
              <label className="label">Total Hectares</label>
              <p className="text">
                {parseFieldArea(order?.total_product_unit)} Ha
              </p>
            </div>
            <div className="right">
              <label className="label">Total Price</label>
              <p className="text">
                {orderTotal.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </p>
              <label className="label">GST Exclusive</label>
            </div>
          </div>
          <div className="text-group">
            <div className="minimum-pricing">
              {order?.minimum_hectare_requirements_pricing
                ? "Your order was subject to minimum hectare pricing; a minimum price was applied."
                : null}
            </div>
          </div>
          <div className="group-divider">Payment Method</div>
          <div className="text-group">
            <div className="left">
              <label className="label">Payment Method</label>
              <p className="text">
                {order?.payment_method === "stripe"
                  ? "Debit or Credit Card"
                  : "Invoice"}
              </p>
            </div>
            <div className="right">
              <label className="label">Payment Status</label>
              <p className="text">{order?.status}</p>
            </div>
          </div>
          <div className="button-group" data-testid="dashboard-button">
            <Button
              onClick={() => history.push("/dashboard")}
              label="Back to Dashboard"
              type="outlined"
              color="white"
              block
            />
            <Button
              onClick={() => setViewReceipt(true)}
              data-testid="view-receipt"
              label={renderFile(order?.status)}
              type="outlined"
              color="white"
              block
              disabled={farmsLoading}
            />
            <Button
              data-testid="place-order"
              onClick={() => history.push("/order/high-resolution-imagery")}
              label="Place New Order"
              type="outlined"
              color="green"
              block
            />
          </div>
        </div>
        {order && (
          <OrderReceipt
            order={order}
            isOpen={viewReceipt}
            onClose={() => setViewReceipt(false)}
            token={token}
            payment={paymentDetails}
            startDate={formatStartDate}
            endDate={formatEndDate}
            farms={farms}
          />
        )}
      </div>
    </div>
  );
};

export default OrderReviewPage;
